import React, { useState, useEffect } from 'react';
import { Amplify, Auth } from 'aws-amplify';
import Intercom from '@intercom/messenger-js-sdk';
import loadable from '@loadable/component';
import {
	Routes, Route, redirect, Link, BrowserRouter,
} from 'react-router-dom';
import AWSConfig from './config';
import loadGtm from './util/loadGtm';
import { HostnameProvider } from './context/HostnameContext';
import { errorCodes, trackEvent } from './models/globals';
import Layout from './components/UI/Layout';
import PrivateComponent from './components/PrivateComponent';
import ErrorNeedsSupport from './components/UI/ErrorNeedsSupport';
import Modal from './components/UI/Modal';
import Loading from './components/UI/Loading';
import { NO_INTERCOM_SUBDOMAINS } from './models/SimpleModels';

Amplify.configure(AWSConfig);

const intercomId = process.env.CLIENT_INTERCOM_ID;
const gaCode = process.env.CLIENT_GA_MEASUREMENT_ID;

const App = loadable(() => import('./App'));
const SignIn = loadable(() => import('./components/Login/BuddySignIn'));
const ConfirmSignIn = loadable(() => import('./components/Login/BuddyConfirmSignIn'));
const ChangePassword = loadable(() => import('./components/Login/BuddyChangePassword'));
const SignUp = loadable(() => import('./components/Login/BuddySignUp'));
const ForgotPassword = loadable(() => import('./components/Login/BuddyForgotPassword'));
const Help = loadable(() => import('./views/Help'));

const HelpPage = ({ props }) => (
	<>
		<Help {...props} />
		<div className="d-flex justify-content-center mt-4">
			<Link className="btn btn-lg btn-primary" to="/sign-i">
				Back to Sign In
			</Link>
		</div>
	</>
);

const getFirstName = (name) => {
	if (!name) {
		return null;
	}
	const nameArr = name.trim().split(' ');
	return nameArr[0];
};

const defaultModalProps = {
	modalShow: false,
	title: '',
	msg: '',
	callback: () => {},
	clickOutsideToClose: true,
	confirmMsg: 'Ok',
	cancelButtonMsg: 'Cancel',
	size: 'lg',
	noButton: false,
	modalShouldConfirm: false,
	customButton: null,
};

function AppWithAuth() {
	const [modal, setModal] = useState(defaultModalProps);
	const [authData, setAuthData] = useState(null);

	const popup = ({ ...newProps }) => {
		setModal({
			...defaultModalProps,
			...newProps,
			modalShow: true,
		});
	};

	const confirm = ({ ...newProps }) => {
		setModal({
			...defaultModalProps,
			...newProps,
			modalShow: true,
			modalShouldConfirm: true,
		});
	};

	const tagAndSendError = (error) => {
		if (window.Sentry) {
			const { Sentry } = window;
			Sentry.captureException(error);
		}
	};

	const signOut = async () => {
		trackEvent('sign_out', 'click');
		try {
			await Auth.signOut();
			setAuthData(null);
			redirect('/sign-in');
		} catch (error) {
			tagAndSendError(error.message || error);
			popup({ msg: error.message || error });
		}
	};
	const openModal = () => {
		setModal((modalProps) => ({ ...modalProps, modalShow: true }));
	};

	const closeModal = () => {
		setModal((modalProps) => ({ ...modalProps, modalShow: false }));
	};

	const displayError = (err) => {
		const { code } = err;
		tagAndSendError(err.message || err);
		if (!code) {
			popup({ msg: <ErrorNeedsSupport error={err.message || err} /> });
			return;
		}
		const match = errorCodes.find((errorCode) => errorCode.code === code);

		if (typeof match === 'undefined') {
			const matchRegEx = /\*\*(.*?)\*\*/;
			const matches = err.message.match(matchRegEx);
			if (err.code === 'UserLambdaValidationException' && matches?.length > 1) {
				popup({
					msg: <ErrorNeedsSupport error={matches[1]} />,
				});
				return;
			}
			popup({
				msg: (
					<ErrorNeedsSupport error="Something went wrong. Please try again later" />
				),
			});
			return;
		}
		popup({ msg: <ErrorNeedsSupport error={match.message} /> });
	};

	const name = authData?.attributes?.name;

	useEffect(() => {
		loadGtm(window, document, 'script', 'dataLayer', gaCode);
		const shouldBootIntercom = !NO_INTERCOM_SUBDOMAINS.some(
			(subdomain) => window.location.hostname.includes(subdomain),
		);
		if (shouldBootIntercom) {
			Intercom({
				app_id: intercomId,
			});
		}
	}, []);

	return (
		<BrowserRouter>
			<HostnameProvider>
				<Layout firstName={getFirstName(name)} signOut={signOut}>
					{modal.modalShow && (
						<Modal
							show={modal.modalShow}
							openModal={openModal}
							closeModal={closeModal}
							modalShouldConfirm={modal.modalShouldConfirm}
							title={modal.title}
							handleConfirmation={modal.callback}
							clickOutsideToClose={modal.clickOutsideToClose}
							size={modal.size}
							confirmMsg={modal.confirmMsg}
							noButton={modal.noButton}
							customButton={modal.customButton}
							trackEvent={trackEvent}
							cancelButtonMsg={modal.cancelButtonMsg}
						>
							{modal.msg}
						</Modal>
					)}
					<Routes>
						<Route
							path="/sign-in"
							element={(
								<SignIn
									default
									displayError={displayError}
									fallback={<Loading />}
									popup={popup}
									trackEvent={trackEvent}
									authData={authData}
									setAuthData={setAuthData}
								/>
							)}
						/>
						<Route
							path="/sign-up"
							element={(
								<SignUp
									displayError={displayError}
									fallback={<Loading />}
									popup={popup}
									trackEvent={trackEvent}
									authData={authData}
									setAuthData={setAuthData}
								/>
							)}
						/>
						<Route
							path="/confirm-sign-in"
							element={(
								<ConfirmSignIn
									displayError={displayError}
									fallback={<Loading />}
									popup={popup}
									trackEvent={trackEvent}
									authData={authData}
									setAuthData={setAuthData}
								/>
							)}
						/>
						<Route
							path="/forgot-password"
							element={(
								<ForgotPassword
									displayError={displayError}
									fallback={<Loading />}
									popup={popup}
									trackEvent={trackEvent}
									authData={authData}
									setAuthData={setAuthData}
								/>
							)}
						/>
						<Route
							path="change-password"
							element={(
								<ChangePassword
									displayError={displayError}
									fallback={<Loading />}
									popup={popup}
									trackEvent={trackEvent}
									authData={authData}
									setAuthData={setAuthData}
								/>
							)}
						/>
						<Route
							path="help"
							element={(
								<HelpPage path="help" fallback={<Loading />} />
							)}
						/>
						<Route
							path="/*"
							element={(
								<SignIn
									default
									displayError={displayError}
									fallback={<Loading />}
									popup={popup}
									trackEvent={trackEvent}
									authData={authData}
									setAuthData={setAuthData}
								/>
							)}
						/>
						<Route
							path="app/*"
							element={(
								<PrivateComponent
									displayError={displayError}
									fallback={<Loading />}
									confirm={confirm}
									closeModal={closeModal}
									popup={popup}
									trackEvent={trackEvent}
									authData={authData}
									tagAndSendError={tagAndSendError}
									setAuthData={setAuthData}
									signOut={signOut}
									component={App}
								/>
							)}
						/>
					</Routes>
				</Layout>
			</HostnameProvider>
		</BrowserRouter>
	);
}

export default AppWithAuth;
